import {
  InboxListItemContent,
  NodeTimeline,
  PollingSpinner,
  RenderNode,
  ViewInvoiceButton,
  WaitingForNextStepItem
} from 'Automations/components'
import { InboxPreview } from '../components/InboxPreview/InboxPreview'
import { match } from 'ts-pattern'
import {
  Text,
  Badge,
  Button,
  GreyGrey80,
  Link,
  Primary14SemiBold,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { getWorkflowDescription } from 'Automations/automationExecutions/utils/getWorkflowDescription'
import { getTasksGroupedByNode } from 'Automations/automationExecutions/overview/getAllTasksGroupedByNode'
import { resolvedNodeDefinitionsAtom } from 'Automations/nodeDefinitions'
import { AutomationExecutionKebabMenu } from 'Automations/automationExecutions/overview/AutomationExecutionKebabMenu'
import { useAutomationExecution } from 'Automations/hooks/useAutomationExecution'
import { useAtomValue } from 'jotai'
import { useExecutionTimeline } from 'Automations/automationExecutions/overview/useExecutionTimeline'
import { CollapsibleToggleItem } from 'Automations/automationExecutions/overview/CollapsibleToggleItem'
import { AnimatePresence, motion } from 'framer-motion'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export const AutomationExecutionSummary = (props: {
  automationExecutionId: string | null
}) => {
  const { isPending, isPolling, automationExecution, triggerType } =
    useAutomationExecution({
      executionId: props.automationExecutionId
    })

  const resolvedNodeDefinitions = useAtomValue(resolvedNodeDefinitionsAtom)

  const nodes = automationExecution
    ? getTasksGroupedByNode(automationExecution)
    : []
  const {
    firstNode,
    collapsibleNodes,
    pinnedNodes,
    hasCollapsibleNodes,
    isExpanded,
    toggle,
    showWaitingItem
  } = useExecutionTimeline({
    nodes,
    isRunning: automationExecution?.status === 'RUNNING',
    executionId: props.automationExecutionId
  })

  // Gate on the resolved registry too: execution summaries are looked up from
  // it, and the async start-node entries are only concrete once resolved.
  if (isPending || !resolvedNodeDefinitions) {
    return <InboxPreview.LoadingState />
  }

  if (!automationExecution) {
    return <InboxPreview.EmptyState />
  }

  // A running execution may briefly have no grouped nodes yet; keep showing the
  // header and "Waiting for next step" rather than a blank empty state.
  if (nodes.length === 0 && automationExecution.status !== 'RUNNING') {
    return <InboxPreview.EmptyState />
  }

  const { title, logo } = getWorkflowDescription(automationExecution)
  const statusBadge = match(automationExecution.status)
    .with('COMPLETED', () => <Badge sentiment="success">Completed</Badge>)
    .with('TERMINATED', 'FAILED', () => (
      <Badge sentiment="error">Rejected</Badge>
    ))
    .with('RUNNING', () => null)
    .exhaustive()

  return (
    <InboxPreview
      header={
        <Grid
          alignItems="center"
          columnGap="8px"
          templateColumns="1fr auto"
          autoFlow="column"
        >
          <Flex alignItems="center" gap="8px">
            <InboxListItemContent.TaskIcon isSelected={false}>
              {logo}
            </InboxListItemContent.TaskIcon>
            <Text
              {...Primary14SemiBold}
              color={GreyGrey80}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {title}
            </Text>
            {statusBadge}
          </Flex>
          <Flex alignItems="center" gap="8px">
            <Box>
              {getTriggerActions({
                triggerType,
                automationExecution
              })}
            </Box>
            <AutomationExecutionKebabMenu
              automationExecutionId={automationExecution.id}
            />
          </Flex>
        </Grid>
      }
    >
      <Box position="absolute" top="8px" right="16px">
        <PollingSpinner isPolling={isPolling} />
      </Box>
      <NodeTimeline data-testid="automations.management.executionsHistory.executionSummary.nodeTimeline">
        {firstNode && (
          <RenderNode
            node={firstNode}
            automationExecution={automationExecution}
            isLatestNodeExecution={
              !hasCollapsibleNodes &&
              pinnedNodes.length === 0 &&
              !showWaitingItem
            }
          />
        )}

        {hasCollapsibleNodes && (
          <>
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {collapsibleNodes.map((node, idx) => (
                    <RenderNode
                      key={node.nodeId}
                      node={node}
                      automationExecution={automationExecution}
                      isLatestNodeExecution={
                        pinnedNodes.length === 0 &&
                        !showWaitingItem &&
                        idx === collapsibleNodes.length - 1
                      }
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <CollapsibleToggleItem
              isExpanded={isExpanded}
              onToggle={toggle}
              data-testid="automations.management.executionsHistory.executionSummary.collapsibleToggle"
            />
          </>
        )}

        {pinnedNodes.map((node, idx) => (
          <RenderNode
            key={node.nodeId}
            node={node}
            automationExecution={automationExecution}
            isLatestNodeExecution={
              !showWaitingItem && idx === pinnedNodes.length - 1
            }
          />
        ))}

        {showWaitingItem && (
          <WaitingForNextStepItem data-testid="automations.management.executionsHistory.executionSummary.waitingForNextStep" />
        )}
      </NodeTimeline>
    </InboxPreview>
  )
}

const getTriggerActions = ({
  triggerType,
  automationExecution
}: {
  triggerType: Dashboardv99990101Api.Automations.TriggerType | undefined
  automationExecution: Dashboardv99990101Api.Automations.AutomationExecution
}) => {
  return match(triggerType)
    .with('QUOTE_REQUIRES_APPROVAL', () => (
      <Button
        data-testid="automations.management.executionsHistory.executionSummary.viewQuote"
        as={Link.Unstyled}
        to={`/quotes/${automationExecution.input?.quoteId}`}
        variant="secondary"
      >
        View quote
      </Button>
    ))
    .with('CONTRACT_UPLOADED', () => {
      const createScheduleTask = automationExecution.tasks.find(
        task =>
          task.inputData.taskExecutorConfiguration?.executor ===
          TASK_EXECUTORS.createSchedule
      )

      if (createScheduleTask?.outputData?.billingScheduleId) {
        return (
          <Button
            data-testid="automations.management.executionsHistory.executionSummary.viewSchedule"
            as={Link.Unstyled}
            to={`/schedules/${createScheduleTask.outputData?.billingScheduleId}`}
            variant="secondary"
          >
            View schedule
          </Button>
        )
      }
    })
    .with('INVOICE_SENT', () => {
      const invoiceId =
        typeof automationExecution.input?.invoiceId === 'string'
          ? automationExecution.input.invoiceId
          : undefined

      if (!invoiceId) {
        return null
      }

      return (
        <ViewInvoiceButton
          invoiceId={invoiceId}
          data-testid="automations.management.executionsHistory.executionSummary.viewInvoice"
        />
      )
    })
    .otherwise(() => 'Workflow execution')
}
